import React, { useEffect } from "react";
import {
  useGame,
  usePlayer,
  useStage,
} from "@empirica/core/player/classic/react";
import { CompletionLink } from "./CompletionLink";
import {
  calculateTotalPay,
  DEFAULT_COMPENSATION_CONFIG,
  roundDollarsToCents,
} from "../../../shared/compensation.js";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";

function dollars(value) {
  return `$${(roundDollarsToCents(value) ?? 0).toFixed(2)}`;
}

function AmountCard({ label, value, muted = false, unavailable = false }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <div className="text-xl text-gray-600">{label}</div>
      <div className={`mt-1 text-3xl font-semibold ${muted ? "text-gray-500" : "text-gray-900"}`}>
        {unavailable ? "$-.--" : dollars(value)}
      </div>
    </div>
  );
}

function useAutoSubmitExitPage() {
  const game = useGame();
  const player = usePlayer();
  const stage = useStage();
  const playerStage = player?.stage;

  useEffect(() => {
    // Only legacy games need to advance remaining pages. The server now ends
    // removal games directly, possibly between this render and its effect.
    if (
      !game || game.hasEnded || game.get("ended") || player?.get("ended") ||
      !stage || stage.get("ended") || !playerStage || player.stage !== playerStage
    ) {
      return;
    }
    if (!playerStage.get("submit")) {
      playerStage.set("submit", true);
    }
  }, [game, player, stage, playerStage]);
}

export function RemovedForInactivityStage() {
  const game = useGame();
  const removal = game.get("inactivityRemoval") || {};
  const lossFrame = isLossFrameDesign(game.get("design"));
  const stage1AlreadyHappened = removal.stage1AlreadyHappened === true;
  const stage2AlreadyHappened = removal.stage2AlreadyHappened === true;

  useAutoSubmitExitPage();

  return (
    <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
      <div className="space-y-3">
        <h2 className="text-4xl font-semibold text-gray-900">Task Ended</h2>
        <div className="rounded-md border border-red-200 bg-red-50 p-5 text-red-900 space-y-3">
          <div className="text-3xl font-semibold">
            You were removed for inactivity.
          </div>
          <p className="text-2xl">
            You did not interact with {removal.maxConsecutiveInactiveStages || 3} pages in a row, 
            so you do not receive payment.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-gray-200 p-5 text-2xl text-gray-700 space-y-3">
        <p>
          Interaction includes clicking page buttons when a page asks you to
          continue, and moving your mouse during the mouse tracking games.
        </p>
        <p>
          You do not need to complete any additional pages.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <AmountCard label="Show-up pay" value={0} muted />
        <AmountCard
          label={lossFrame ? "Game 1 money kept" : "Game 1 earnings"}
          value={0}
          unavailable={!stage1AlreadyHappened}
          muted
        />
        <AmountCard
          label={lossFrame ? "Game 2 money kept" : "Game 2 earnings"}
          value={0}
          unavailable={!stage2AlreadyHappened}
          muted
        />
      </div>

      <CompletionLink unpaid embedded />
    </div>
  );
}

export function PartnerRemovedForInactivityStage() {
  const game = useGame();
  const player = usePlayer();
  const removal = game.get("inactivityRemoval") || {};
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);

  useAutoSubmitExitPage();

  const showUpPay = Number(
    removal.showUpPay ?? design.basePay ?? design.showUpPayment ?? DEFAULT_COMPENSATION_CONFIG.basePay
  );
  const baseBonus = Number(removal.baseBonus ?? design.baseBonus ?? DEFAULT_COMPENSATION_CONFIG.baseBonus);
  const stage1Earnings = Number(removal.partnerStage1Earnings ?? player.get("stage1Earnings") ?? 0);
  const stage2Contribution = Number(removal.partnerStage2Contribution ?? player.get("stage2Earnings") ?? 0);
  const stage1AlreadyHappened = removal.stage1AlreadyHappened ?? Boolean(game.get("stage1"));
  const stage2AlreadyHappened = removal.stage2AlreadyHappened ?? Boolean(game.get("stage2"));
  const totalPay = Number(
    removal.partnerTotal ??
      player.get("finalPay") ??
      calculateTotalPay({
        basePay: showUpPay,
        baseBonus,
        stage1Earnings,
        stage2Earnings: stage2Contribution,
      })
  );

  return (
    <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">
      <div className="space-y-3">
        <h2 className="text-4xl font-semibold text-gray-900">Task Ended</h2>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-5 text-amber-900 space-y-3">
          <div className="text-3xl font-semibold">
            Your {counterpart.term} was removed for inactivity.
          </div>
          <p className="text-2xl">
            Your {counterpart.term} did not interact with {removal.maxConsecutiveInactiveStages || 3} pages in a row,
            so the task has ended early.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-5 text-emerald-900">
        <div className="text-2xl font-semibold">Your total pay</div>
        <div className="mt-1 text-5xl font-bold text-emerald-950">
          {dollars(totalPay)}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <AmountCard label="Show-up pay" value={showUpPay} />
        <AmountCard
          label={lossFrame ? "Game 1 money kept" : "Game 1 earnings"}
          value={stage1Earnings}
          unavailable={!stage1AlreadyHappened}
          muted={!stage1AlreadyHappened}
        />
        <AmountCard
          label={lossFrame ? "Game 2 money kept" : "Game 2 earnings"}
          value={stage2Contribution}
          unavailable={!stage2AlreadyHappened}
          muted={!stage2AlreadyHappened}
        />
      </div>

      <div className="rounded-md border border-gray-200 p-5 text-2xl text-gray-700 space-y-3">
        <p>
          You do not need to complete any additional pages.
        </p>
      </div>

      <CompletionLink embedded />
    </div>
  );
}
